// Home.jsx
// DL 09/19/2k22

// Packages, Dependencies, and Shared Libraries
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { openInNewTab } from "../sharedFunctions.js";

// React Components
import HomePageHeader from "./HomePageHeader.jsx";
import RandomPlantSection from "./RandomPlantSection.jsx";
import FeaturedWindows from "./FeaturedWindows.jsx";

// Images
import appStoreBadge from "../Images/PlantBioPage/appStoreDownload.png";
import plantAtComputer from "../Images/Graphics/plantAtComputer.png";
import gardenerPlant from "../Images/PlantBioPage/gardenerPlant.png";
import tanning from "../Images/PlantBioPage/tanning.png";
import logo from "../Images/HomePage/LeafLings.png";
import instagramLogo from "../Images/SocialMedia/instagram_glyph.png";
import tiktokLogo from "../Images/SocialMedia/tiktokLogo.png";
import SocialMediaIcon from "./SocialMediaIcon.jsx";

// Constants
import * as constants from "../constants";

// The three marketing feature cards shown under the hero
const FEATURES = [
  {
    image: plantAtComputer,
    title: "Chat with your plants",
    body:
      "Every Leafling has its own personality. Ask how they're feeling, get " +
      "care advice, or just catch up — they always listen and never judge.",
  },
  {
    image: gardenerPlant,
    title: "Care made simple",
    body:
      "Watering reminders, light readings, and species-specific care guides " +
      "keep your plants thriving — no green thumb required.",
  },
  {
    image: tanning,
    title: "Share your garden",
    body:
      "Every Leafling gets its own page, and your whole window lives online " +
      "too. Send a link over iMessage or WhatsApp and friends can meet your " +
      "plants — no app needed.",
  },
];

/*******************************************************************************
 * @func Home (React Component)
 * The landing page rendered at the root path. Introduces Leaflings, shows off
 * a real plant from the database, and directs visitors to the iOS app.
 *
 * @param None
 * @return JSX Element
 *
 * @notes Also handles the legacy /?id=<plantID> URL style by redirecting to
 * /plant/<plantID> (old QR tags still use it).
 *
 ******************************************************************************/
const Home = () => {
  // Constants
  const navigate = useNavigate();

  // Handle re-routing if it uses the old url style, fires once upon render of
  // the component. Redirects to the /plant/id route
  useEffect(() => {
    // Get the ID
    const myURL = new URL(window.location.href);
    const id = myURL.searchParams.get("id");
    // Re route to new one
    if (id !== null) {
      navigate("/plant/" + id);
    }
    // eslint-disable-next-line
  }, []); // End useEffect

  return (
    <div className="font-global text-leaf-brown">
      <HomePageHeader />

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-14 sm:pt-16 flex flex-col md:flex-row items-center gap-10">
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight text-leaf-green-dark">
            Your plants,
            <span className="block font-header font-normal text-leaf-green mt-3 pb-2">
              brought to life
            </span>
          </h1>
          <p className="mt-4 text-lg max-w-md mx-auto md:mx-0 text-leaf-brown">
            Leaflings gives your houseplants a face, a voice, and a
            personality. Chat with your plants, get watering reminders and
            care tips, and watch them grow — right from your phone.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
            <img
              src={appStoreBadge}
              alt="Download Leaflings on the App Store"
              className="h-[52px] cursor-pointer hover:opacity-85 transition-opacity"
              onClick={() => openInNewTab(constants.APP_STORE_URL)}
            />
            <button
              className="text-leaf-green-dark font-bold underline underline-offset-4 hover:text-leaf-green transition-colors"
              onClick={() => navigate("/random")}
            >
              or meet a random Leafling →
            </button>
          </div>
          <button
            className="mt-5 inline-flex items-center gap-2 bg-leaf-green-soft border border-leaf-green/40 text-leaf-green-deep font-bold text-sm rounded-full px-4 py-2 hover:bg-leaf-green hover:text-white transition-colors"
            onClick={() => navigate("/game")}
          >
            🌸 New — play Bloom &amp; Blight, the Leaflings card game
          </button>
        </div>
        <div className="flex-1 w-full">
          <RandomPlantSection onLoadCallback={() => {}} />
        </div>
      </section>

      {/* Real windows from real gardeners (renders only when found) */}
      <FeaturedWindows />

      {/* Features */}
      <section className="border-y border-leaf-brown/10 py-14">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-center font-header text-3xl text-leaf-green-dark mb-10">
            How it works
          </h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="bg-global-accent rounded-3xl shadow-card border border-leaf-brown/10 p-6 text-center flex flex-col items-center"
              >
                <img
                  src={feature.image}
                  alt=""
                  className="h-32 object-contain mb-4"
                />
                <h3 className="text-xl font-extrabold text-leaf-green-dark mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed text-leaf-brown">
                  {feature.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bloom & Blight showcase */}
      <section className="mx-auto max-w-5xl px-4 py-14">
        <div
          className="relative overflow-hidden rounded-3xl shadow-lift border-4 border-white"
          style={{
            backgroundImage: "url(/game-assets/garden-room.png)",
            backgroundSize: "cover",
            backgroundPosition: "center 70%",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(35,25,10,0.72)] via-[rgba(35,25,10,0.45)] to-transparent" />
          <div className="relative p-8 sm:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-white">
              <div className="inline-block bg-leaf-green text-white text-xs font-bold rounded-full px-3 py-1 mb-3 tracking-wide">
                NEW · FREE IN YOUR BROWSER
              </div>
              <h2 className="font-header text-4xl mb-3 pb-1">
                Bloom &amp; Blight
              </h2>
              <p className="text-white/90 max-w-md leading-relaxed">
                The Leaflings card game. Plant, water, and weather the
                forecast — grow five blooms before your rival does. Play solo
                against Sprout, or send a friend a join code and battle
                head-to-head.
              </p>
              <button
                className="mt-5 bg-white text-leaf-green-dark font-bold rounded-full px-6 py-2.5 shadow-card hover:bg-leaf-cream transition-colors"
                onClick={() => navigate("/game")}
              >
                🌸 Play now — it's free
              </button>
            </div>
            <div className="flex items-end gap-2 shrink-0">
              <img
                src="/game-assets/calathea.png"
                alt=""
                className="h-36 sm:h-44 object-contain drop-shadow-lg"
              />
              <img
                src="/game-assets/snake.png"
                alt=""
                className="h-28 sm:h-36 object-contain drop-shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Your garden, online */}
      <section className="bg-body-bg border-y border-leaf-brown/10 py-14">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-header text-3xl text-leaf-green-dark mb-3 pb-1">
            Already growing with us?
          </h2>
          <p className="text-leaf-brown max-w-xl mx-auto">
            Sign in with your Leaflings account to see your own window and
            plants online — and share them with anyone, anywhere.
          </p>
          <button
            className="mt-5 bg-leaf-green-dark text-white font-bold rounded-full px-6 py-2.5 shadow-card hover:bg-leaf-green transition-colors"
            onClick={() => navigate("/me")}
          >
            See my garden →
          </button>
        </div>
      </section>

      {/* About / SEO copy */}
      <section className="mx-auto max-w-3xl px-4 py-14 text-center">
        <h2 className="font-header text-3xl text-leaf-green-dark mb-4">
          What is Leaflings?
        </h2>
        <p className="leading-relaxed">{constants.HOME_PAGE_DESC}</p>
      </section>

      {/* Download CTA band */}
      <section className="bg-leaf-green-dark text-white py-14">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-header text-3xl mb-3 pb-1">
            Ready to say hello?
          </h2>
          <p className="text-white/90 mb-6">
            Download Leaflings for iPhone and meet your plants for the first
            time.
          </p>
          <img
            src={appStoreBadge}
            alt="Download Leaflings on the App Store"
            className="h-[56px] mx-auto cursor-pointer hover:opacity-85 transition-opacity"
            onClick={() => openInNewTab(constants.APP_STORE_URL)}
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-global-bg py-10">
        <div className="mx-auto max-w-5xl px-4 flex flex-col items-center gap-4">
          <img src={logo} alt="Leaflings" className="w-[150px]" />
          <div className="flex items-center gap-4">
            <SocialMediaIcon
              logoRelPath={instagramLogo}
              link={constants.INSTAGRAM_URL}
            />
            <SocialMediaIcon
              logoRelPath={tiktokLogo}
              link={constants.TIKTOK_URL}
            />
          </div>
          <div className="text-sm text-leaf-brown-soft text-center">
            <span
              className="underline cursor-pointer hover:text-leaf-brown transition-colors"
              onClick={() => openInNewTab(constants.INSTAGRAM_URL)}
            >
              DM us on Instagram
            </span>
            {" · "}
            <span
              className="underline cursor-pointer hover:text-leaf-brown transition-colors"
              onClick={() => navigate("/random")}
            >
              Meet a random Leafling
            </span>
          </div>
          <div className="text-xs text-leaf-brown-soft">
            © {new Date().getFullYear()} Leaflings
          </div>
        </div>
      </footer>
    </div>
  );
}; // End Home

export default Home;
